import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Paper, Box, Typography, AppBar, Toolbar, IconButton, Button, Grid,
  Card, CardContent, Chip, Divider, Alert, CircularProgress, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogContent, DialogTitle, DialogActions, TextField,
  MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import {
  ArrowBack as BackIcon, Edit as EditIcon, Download as DownloadIcon,
  Print as PrintIcon, Add as AddIcon
} from '@mui/icons-material';
import { laptopAPI, historyAPI, repairAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const LaptopDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { admin } = useAuth();
  const [laptop, setLaptop] = useState(null);
  const [history, setHistory] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);

  // Dialog states
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [repairDialogOpen, setRepairDialogOpen] = useState(false);

  // Form states
  const [assignmentForm, setAssignmentForm] = useState({
    assignedTo: '',
    assignedToName: '',
    status: 'assigned',
    notes: ''
  });

  const [configForm, setConfigForm] = useState({
    component: 'ram',
    oldValue: '',
    newValue: '',
    notes: ''
  });

  const [repairForm, setRepairForm] = useState({
    issueDescription: '',
    serviceProvider: '',
    repairType: 'paid',
    status: 'pending',
    sentDate: new Date().toISOString().split('T')[0],
    expectedReturnDate: '',
    actualReturnDate: '',
    cost: 0,
    laborCost: 0,
    invoiceNumber: '',
    repairNotes: '',
    billFile: null
  });

  useEffect(() => {
    loadLaptopData();
  }, [id]);

  const loadLaptopData = async () => {
    try {
      setLoading(true);

      const laptopResponse = await laptopAPI.getById(id);
      setLaptop(laptopResponse.data.data);

      const historyResponse = await historyAPI.getLaptopHistory(id);
      setHistory(historyResponse.data.data || []);

      const repairsResponse = await repairAPI.getAll({ laptopId: id });
      setRepairs(repairsResponse.data.data || []);

      setError('');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to load laptop details');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAssignment = async () => {
    try {
      const changes = [];

      if (assignmentForm.assignedTo) {
        changes.push({
          field: 'primaryUserEmail',
          oldValue: laptop.primaryUserEmail || null,
          newValue: assignmentForm.assignedTo
        });
      }

      if (assignmentForm.assignedToName) {
        changes.push({
          field: 'primaryUserDisplayName',
          oldValue: laptop.primaryUserDisplayName || null,
          newValue: assignmentForm.assignedToName
        });
      }

      changes.push({
        field: 'status',
        oldValue: laptop.status,
        newValue: assignmentForm.status
      });

      await api.post('/history', {
        laptopId: id,
        changeType: 'assignment',
        changes,
        notes: assignmentForm.notes
      });

      setAssignmentDialogOpen(false);
      setAssignmentForm({ assignedTo: '', assignedToName: '', status: 'assigned', notes: '' });
      loadLaptopData();
    } catch (error) {
      alert('Failed to add assignment: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleAddConfigChange = async () => {
    try {
      await api.post('/history', {
        laptopId: id,
        changeType: 'specification_change',
        changes: [{
          field: configForm.component,
          oldValue: configForm.oldValue,
          newValue: configForm.newValue
        }],
        notes: configForm.notes
      });

      setConfigDialogOpen(false);
      setConfigForm({ component: 'ram', oldValue: '', newValue: '', notes: '' });
      loadLaptopData();
    } catch (error) {
      alert('Failed to add configuration change: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleAddRepair = async () => {
    try {
      const formData = new FormData();

      // Add all form fields except billFile
      Object.keys(repairForm).forEach(key => {
        if (key === 'billFile' && repairForm[key]) {
          formData.append('bill', repairForm[key]);
        } else if (key !== 'billFile') {
          formData.append(key, repairForm[key]);
        }
      });
      formData.append('laptopId', id);

      await api.post('/repairs', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setRepairDialogOpen(false);
      setRepairForm({
        issueDescription: '',
        serviceProvider: '',
        repairType: 'paid',
        status: 'pending',
        sentDate: new Date().toISOString().split('T')[0],
        expectedReturnDate: '',
        actualReturnDate: '',
        cost: 0,
        laborCost: 0,
        invoiceNumber: '',
        repairNotes: '',
        billFile: null
      });
      loadLaptopData();
    } catch (error) {
      alert('Failed to add repair: ' + (error.response?.data?.message || error.message));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      assigned: 'success',
      unassigned: 'warning',
      'in-repair': 'error',
      retired: 'default'
    };
    return colors[status] || 'default';
  };

  const getRepairStatusColor = (status) => {
    const colors = {
      completed: 'success',
      'in-progress': 'info',
      pending: 'warning'
    };
    return colors[status] || 'default';
  };

  const downloadQRCode = () => {
    if (!laptop?.qrCode) return;
    const link = document.createElement('a');
    link.href = laptop.qrCode;
    link.download = `${laptop.serialNumber}_QRCode.png`;
    link.click();
  };

  const printQRCode = () => {
    if (!laptop?.qrCode) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head><title>QR Code - ${laptop.serialNumber}</title>
          <style>
            body { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; margin: 0; font-family: Arial, sans-serif; }
            img { max-width: 400px; margin: 20px; }
            .info { text-align: center; margin-top: 20px; }
          </style>
        </head>
        <body>
          <img src="${laptop.qrCode}" alt="QR Code" />
          <div class="info">
            <h2>${laptop.deviceName}</h2>
            <p><strong>Serial Number:</strong> ${laptop.serialNumber}</p>
            <p><strong>Model:</strong> ${laptop.manufacturer} ${laptop.model}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
  };

  const formatDate = (date) => new Date(date).toLocaleString();

  // Helper function to determine currency based on location
  const determineCurrency = (location) => {
    if (!location) return 'USD';

    const locationLower = location.toLowerCase();
    const indianLocations = ['india', 'bangalore', 'bengaluru', 'mumbai', 'delhi', 'hyderabad',
      'chennai', 'kolkata', 'pune', 'ahmedabad', 'jaipur', 'lucknow', 'kanpur', 'nagpur',
      'indore', 'thane', 'bhopal', 'visakhapatnam', 'pimpri', 'patna', 'vadodara', 'ghaziabad',
      'ludhiana', 'agra', 'nashik', 'faridabad', 'meerut', 'rajkot', 'kalyan', 'vasai',
      'varanasi', 'srinagar', 'aurangabad', 'dhanbad', 'amritsar', 'navi mumbai', 'allahabad',
      'ranchi', 'howrah', 'coimbatore', 'jabalpur', 'gwalior', 'vijayawada', 'jodhpur',
      'madurai', 'raipur', 'kota', 'guwahati', 'chandigarh', 'solapur', 'hubballi', 'tiruchirappalli',
      'tiruppur', 'moradabad', 'mysore', 'bareilly', 'gurgaon', 'aligarh', 'jalandhar', 'bhubaneswar',
      'salem', 'warangal', 'guntur', 'bhiwandi', 'saharanpur', 'gorakhpur', 'bikaner', 'amravati',
      'noida', 'jamshedpur', 'bhilai', 'cuttack', 'firozabad', 'kochi', 'nellore', 'bhavnagar',
      'dehradun', 'durgapur', 'asansol', 'rourkela', 'nanded', 'kolhapur', 'ajmer', 'akola',
      'gulbarga', 'jamnagar', 'ujjain', 'loni', 'siliguri', 'jhansi', 'ulhasnagar', 'jammu',
      'sangli', 'mangalore', 'erode', 'belgaum', 'ambattur', 'tirunelveli', 'malegaon', 'gaya'];

    const isIndia = indianLocations.some(city => locationLower.includes(city));
    return isIndia ? 'INR' : 'USD';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !laptop) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>{error || 'Laptop not found'}</Alert>
        <Button onClick={() => navigate('/laptops')} sx={{ mt: 2 }}>Back to List</Button>
      </Container>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/laptops')} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Laptop Details
          </Typography>
          <Button color="inherit" startIcon={<EditIcon />} onClick={() => navigate(`/laptops/${id}/edit`)}>
            Edit
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">{laptop.deviceName}</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip label={laptop.status} color={getStatusColor(laptop.status)} />
                </Box>
              </Box>
              {laptop.tags && laptop.tags.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
                  {laptop.tags.map((tag, index) => (
                    <Chip key={index} label={tag} size="small" variant="outlined" color="primary" />
                  ))}
                </Box>
              )}
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Serial Number</Typography>
                  <Typography variant="body1" gutterBottom>{laptop.serialNumber}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Manufacturer</Typography>
                  <Typography variant="body1" gutterBottom>{laptop.manufacturer}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Model</Typography>
                  <Typography variant="body1" gutterBottom>{laptop.model}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Processor</Typography>
                  <Typography variant="body1" gutterBottom>{laptop.processor}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">RAM</Typography>
                  <Typography variant="body1" gutterBottom>{laptop.ram || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Storage</Typography>
                  <Typography variant="body1" gutterBottom>{laptop.storage || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Operating System</Typography>
                  <Typography variant="body1" gutterBottom>{laptop.os}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Location</Typography>
                  <Typography variant="body1" gutterBottom>{laptop.location || 'N/A'}</Typography>
                </Grid>
                {laptop.primaryUserEmail && (
                  <>
                    <Grid item xs={12}><Divider sx={{ my: 1 }} /><Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Assigned User</Typography></Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Name</Typography>
                      <Typography variant="body1" gutterBottom>{laptop.primaryUserDisplayName || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Email</Typography>
                      <Typography variant="body1" gutterBottom>{laptop.primaryUserEmail}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Paper>

            <Paper sx={{ mb: 3 }}>
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                <Tab label="Default Configuration" />
                <Tab label="Configuration Changes" />
                <Tab label="Assignment History" />
                <Tab label="Repair History" />
              </Tabs>

              {activeTab === 0 && (
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>Hardware Specifications</Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {[
                      ['Serial Number', laptop.serialNumber],
                      ['Device Name', laptop.deviceName],
                      ['Manufacturer', laptop.manufacturer],
                      ['Model', laptop.model],
                      ['Processor', laptop.processor],
                      ['RAM', laptop.ram || 'Not specified'],
                      ['Storage', laptop.storage || 'Not specified'],
                      ['Operating System', laptop.os],
                      ['SKU Family', laptop.skuFamily || 'Not specified'],
                      ['Location', laptop.location || 'Not specified']
                    ].map(([label, value]) => (
                      <Grid item xs={12} sm={6} key={label}>
                        <Typography variant="body2" color="text.secondary">{label}</Typography>
                        <Typography variant="body1" gutterBottom>{value}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {activeTab === 1 && (
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">Configuration Changes</Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      size="small"
                      onClick={() => setConfigDialogOpen(true)}
                    >
                      Add Change
                    </Button>
                  </Box>
                  {history.filter(r => r.changeType === 'specification_change' ||
                    r.changes.some(c => ['ram', 'storage', 'processor', 'os', 'skufamily'].includes(c.field?.toLowerCase()))
                  ).length === 0 ? (
                    <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                      No configuration changes recorded yet
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Component</TableCell>
                            <TableCell>Old Value</TableCell>
                            <TableCell>New Value</TableCell>
                            <TableCell>Changed By</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {history
                            .filter(r => r.changeType === 'specification_change' ||
                              r.changes.some(c => ['ram', 'storage', 'processor', 'os', 'skufamily'].includes(c.field?.toLowerCase()))
                            )
                            .flatMap(record =>
                              record.changes
                                .filter(c => ['ram', 'storage', 'processor', 'os', 'skufamily'].includes(c.field?.toLowerCase()))
                                .map((change, idx) => (
                                  <TableRow key={`${record._id}-${idx}`}>
                                    <TableCell>{formatDate(record.timestamp || record.createdAt)}</TableCell>
                                    <TableCell><Chip label={change.field} size="small" color="primary" /></TableCell>
                                    <TableCell>{change.oldValue || 'N/A'}</TableCell>
                                    <TableCell><strong>{change.newValue}</strong></TableCell>
                                    <TableCell>{record.changedByName}</TableCell>
                                  </TableRow>
                                ))
                            )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {activeTab === 2 && (
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">Assignment History</Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      size="small"
                      onClick={() => setAssignmentDialogOpen(true)}
                    >
                      Add Assignment
                    </Button>
                  </Box>
                  {history.filter(r => r.changeType === 'assignment' ||
                    r.changes.some(c => ['primaryuseremail', 'primaryuserdisplayname', 'status'].includes(c.field?.toLowerCase()))
                  ).length === 0 ? (
                    <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                      No assignment history available
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Action</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>IT Member</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {history
                            .filter(r => r.changeType === 'assignment' ||
                              r.changes.some(c => ['primaryuseremail', 'primaryuserdisplayname', 'status'].includes(c.field?.toLowerCase()))
                            )
                            .map((record) => {
                              const userChange = record.changes.find(c =>
                                c.field?.toLowerCase() === 'primaryuseremail' || c.field?.toLowerCase() === 'primaryuserdisplayname'
                              );
                              const statusChange = record.changes.find(c => c.field?.toLowerCase() === 'status');

                              return (
                                <TableRow key={record._id}>
                                  <TableCell>{formatDate(record.timestamp || record.createdAt)}</TableCell>
                                  <TableCell>
                                    <Chip label={record.changeType} size="small" color={statusChange?.newValue === 'assigned' ? 'success' : 'warning'} />
                                  </TableCell>
                                  <TableCell>
                                    {userChange ? (
                                      <Box>
                                        <Typography variant="body2">{userChange.newValue || 'Unassigned'}</Typography>
                                        {userChange.oldValue && (
                                          <Typography variant="caption" color="text.secondary">
                                            Previously: {userChange.oldValue}
                                          </Typography>
                                        )}
                                      </Box>
                                    ) : 'No user change'}
                                  </TableCell>
                                  <TableCell>
                                    {statusChange ? (
                                      <Box>
                                        <Chip label={statusChange.newValue} size="small" color={getStatusColor(statusChange.newValue)} />
                                        {statusChange.oldValue && (
                                          <Typography variant="caption" color="text.secondary" display="block">
                                            Was: {statusChange.oldValue}
                                          </Typography>
                                        )}
                                      </Box>
                                    ) : 'No status change'}
                                  </TableCell>
                                  <TableCell>{record.changedByName}</TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {activeTab === 3 && (
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">Repair History</Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      size="small"
                      onClick={() => setRepairDialogOpen(true)}
                    >
                      Add Repair
                    </Button>
                  </Box>
                  {repairs.length === 0 ? (
                    <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                      No repair history available
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Issue</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Cost</TableCell>
                            <TableCell>Bill</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {repairs.map((repair) => (
                            <TableRow key={repair._id}>
                              <TableCell>
                                <Typography variant="body2">{formatDate(repair.sentDate)}</Typography>
                                {repair.actualReturnDate && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Returned: {new Date(repair.actualReturnDate).toLocaleDateString()}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2"><strong>{repair.issueDescription?.substring(0, 50)}</strong></Typography>
                                {repair.serviceProvider && (
                                  <Typography variant="caption" color="text.secondary" display="block">{repair.serviceProvider}</Typography>
                                )}
                                {repair.invoiceNumber && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Invoice: {repair.invoiceNumber}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell><Chip label={repair.repairType} size="small" /></TableCell>
                              <TableCell>
                                <Chip label={repair.status} color={getRepairStatusColor(repair.status)} size="small" />
                              </TableCell>
                              <TableCell>
                                {repair.cost ? (
                                  <Box>
                                    <Typography variant="body2">
                                      {repair.currency === 'INR' ? '₹' : '$'}{repair.cost.toFixed(2)}
                                    </Typography>
                                    {repair.laborCost > 0 && (
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        Labor: {repair.currency === 'INR' ? '₹' : '$'}{repair.laborCost.toFixed(2)}
                                      </Typography>
                                    )}
                                  </Box>
                                ) : 'N/A'}
                              </TableCell>
                              <TableCell>
                                {repair.billUrl ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    href={repair.billUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    View
                                  </Button>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">No bill</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>QR Code</Typography>
              <Divider sx={{ my: 2 }} />
              {laptop.qrCode ? (
                <>
                  <Box
                    component="img"
                    src={laptop.qrCode}
                    alt="Laptop QR Code"
                    sx={{ width: '100%', maxWidth: 300, height: 'auto', cursor: 'pointer', border: '2px solid #e0e0e0', borderRadius: 1, p: 1 }}
                    onClick={() => setQrDialogOpen(true)}
                  />
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadQRCode} size="small">Download</Button>
                    <Button variant="outlined" startIcon={<PrintIcon />} onClick={printQRCode} size="small">Print</Button>
                  </Box>
                </>
              ) : (
                <Alert severity="info">No QR code generated yet</Alert>
              )}
            </Paper>

            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Summary</Typography>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ mt: 2 }}>
                  {[
                    ['Total Changes', history.length],
                    ['Total Repairs', repairs.length],
                    ['Active Repairs', repairs.filter(r => r.status === 'in-progress' || r.status === 'pending').length],
                    ['Total Repair Cost', `$${repairs.reduce((sum, r) => sum + (r.cost || 0), 0).toFixed(2)}`]
                  ].map(([label, value]) => (
                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">{label}</Typography>
                      <Typography variant="body2">{value}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Add Assignment Dialog */}
      <Dialog open={assignmentDialogOpen} onClose={() => setAssignmentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Assignment History</DialogTitle>
        <DialogContent>
          <TextField
            label="Assigned To (Email)"
            fullWidth
            margin="normal"
            value={assignmentForm.assignedTo}
            onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedTo: e.target.value })}
          />
          <TextField
            label="Assigned To (Name)"
            fullWidth
            margin="normal"
            value={assignmentForm.assignedToName}
            onChange={(e) => setAssignmentForm({ ...assignmentForm, assignedToName: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={assignmentForm.status}
              onChange={(e) => setAssignmentForm({ ...assignmentForm, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="assigned">Assigned</MenuItem>
              <MenuItem value="unassigned">Unassigned</MenuItem>
              <MenuItem value="in-repair">In Repair</MenuItem>
              <MenuItem value="retired">Retired</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Notes (optional)"
            fullWidth
            multiline
            rows={3}
            margin="normal"
            value={assignmentForm.notes}
            onChange={(e) => setAssignmentForm({ ...assignmentForm, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignmentDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddAssignment} variant="contained" color="primary">Add Assignment</Button>
        </DialogActions>
      </Dialog>

      {/* Add Configuration Change Dialog */}
      <Dialog open={configDialogOpen} onClose={() => setConfigDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Configuration Change</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Component</InputLabel>
            <Select
              value={configForm.component}
              onChange={(e) => setConfigForm({ ...configForm, component: e.target.value })}
              label="Component"
            >
              <MenuItem value="ram">RAM</MenuItem>
              <MenuItem value="storage">Storage</MenuItem>
              <MenuItem value="processor">Processor</MenuItem>
              <MenuItem value="os">Operating System</MenuItem>
              <MenuItem value="skufamily">SKU Family</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Old Value"
            fullWidth
            margin="normal"
            value={configForm.oldValue}
            onChange={(e) => setConfigForm({ ...configForm, oldValue: e.target.value })}
          />
          <TextField
            label="New Value"
            fullWidth
            margin="normal"
            required
            value={configForm.newValue}
            onChange={(e) => setConfigForm({ ...configForm, newValue: e.target.value })}
          />
          <TextField
            label="Notes (optional)"
            fullWidth
            multiline
            rows={3}
            margin="normal"
            value={configForm.notes}
            onChange={(e) => setConfigForm({ ...configForm, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddConfigChange} variant="contained" color="primary">Add Change</Button>
        </DialogActions>
      </Dialog>

      {/* Add Repair Dialog */}
      <Dialog open={repairDialogOpen} onClose={() => setRepairDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Repair Record</DialogTitle>
        <DialogContent>
          <TextField
            label="Issue Description"
            fullWidth
            multiline
            rows={3}
            margin="normal"
            required
            value={repairForm.issueDescription}
            onChange={(e) => setRepairForm({ ...repairForm, issueDescription: e.target.value })}
          />
          <TextField
            label="Service Provider"
            fullWidth
            margin="normal"
            value={repairForm.serviceProvider}
            onChange={(e) => setRepairForm({ ...repairForm, serviceProvider: e.target.value })}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Repair Type</InputLabel>
                <Select
                  value={repairForm.repairType}
                  onChange={(e) => setRepairForm({ ...repairForm, repairType: e.target.value })}
                  label="Repair Type"
                >
                  <MenuItem value="warranty">Warranty</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="internal">Internal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  value={repairForm.status}
                  onChange={(e) => setRepairForm({ ...repairForm, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in-progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sent Date"
                type="date"
                fullWidth
                margin="normal"
                value={repairForm.sentDate}
                onChange={(e) => setRepairForm({ ...repairForm, sentDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Expected Return Date"
                type="date"
                fullWidth
                margin="normal"
                value={repairForm.expectedReturnDate}
                onChange={(e) => setRepairForm({ ...repairForm, expectedReturnDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <TextField
            label="Actual Return Date"
            type="date"
            fullWidth
            margin="normal"
            value={repairForm.actualReturnDate}
            onChange={(e) => setRepairForm({ ...repairForm, actualReturnDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            helperText="Leave empty if repair is not completed yet"
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label={`Cost (${laptop ? determineCurrency(laptop.location) : 'USD'})`}
                type="number"
                fullWidth
                margin="normal"
                value={repairForm.cost}
                onChange={(e) => setRepairForm({ ...repairForm, cost: parseFloat(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={`Labor Cost (${laptop ? determineCurrency(laptop.location) : 'USD'})`}
                type="number"
                fullWidth
                margin="normal"
                value={repairForm.laborCost}
                onChange={(e) => setRepairForm({ ...repairForm, laborCost: parseFloat(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Invoice Number"
                fullWidth
                margin="normal"
                value={repairForm.invoiceNumber}
                onChange={(e) => setRepairForm({ ...repairForm, invoiceNumber: e.target.value })}
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Upload Repair Bill (Images or PDF, max 5MB)
            </Typography>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mt: 1 }}
            >
              {repairForm.billFile ? repairForm.billFile.name : 'Choose File'}
              <input
                type="file"
                hidden
                accept="image/*,.pdf"
                onChange={(e) => setRepairForm({ ...repairForm, billFile: e.target.files[0] })}
              />
            </Button>
            {repairForm.billFile && (
              <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                File selected: {repairForm.billFile.name} ({(repairForm.billFile.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            )}
          </Box>
          <TextField
            label="Repair Notes (optional)"
            fullWidth
            multiline
            rows={3}
            margin="normal"
            value={repairForm.repairNotes}
            onChange={(e) => setRepairForm({ ...repairForm, repairNotes: e.target.value })}
          />
          <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" color="info.dark">
              <strong>Currency:</strong> {laptop ? determineCurrency(laptop.location) : 'USD'} (auto-detected based on laptop location: {laptop?.location || 'N/A'})
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRepairDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddRepair} variant="contained" color="primary">Add Repair</Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>QR Code - {laptop.serialNumber}</DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Box
            component="img"
            src={laptop.qrCode}
            alt="Laptop QR Code"
            sx={{ width: '100%', maxWidth: 400, height: 'auto' }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LaptopDetails;
